import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, Check, X, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

export default function MollieSimulate() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const payment_id = params.get("payment_id");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!payment_id) nav("/pro/choose-plan", { replace: true });
  }, [payment_id, nav]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/mollie/confirm-mock", null, {
        params: { payment_id },
      });
      setUser(data.user);
      setDone(true);
      track("premium_activated", { provider: "mollie", mode: "mock", value: 9.99, currency: "EUR" });
      toast.success("Paiement confirmé — bienvenue en Premium !");
      setTimeout(() => nav("/pro/home", { replace: true }), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const cancel = () => {
    nav("/pro/choose-plan?canceled=1", { replace: true });
  };

  return (
    <div className="app-shell grain-bg flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md rounded-3xl bg-white border border-border shadow-sm p-6" data-testid="mollie-simulate">
        <div className="flex items-center gap-2 mb-4 text-xs text-slate2">
          <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          Passerelle de paiement — mode démo
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-2xl bg-pro text-brand flex items-center justify-center">
            <Crown className="w-6 h-6" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg text-ink">Premium — 9,99 €</div>
            <div className="text-xs text-slate2">Prélèvement mensuel · Mollie</div>
          </div>
        </div>

        <div className="rounded-2xl bg-bone p-4 mb-5 text-xs text-slate2 flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          Cette page simule le tunnel de paiement Mollie. Une fois la clé Mollie ajoutée,
          vous serez redirigé vers la vraie passerelle sécurisée.
        </div>

        {done ? (
          <div className="rounded-2xl bg-green-50 border border-green-200 p-4 flex items-center gap-3 text-green-800" data-testid="mollie-simulate-success">
            <Check className="w-5 h-5" />
            <div className="text-sm font-medium">Paiement confirmé, redirection…</div>
          </div>
        ) : (
          <div className="space-y-2">
            <Button
              onClick={confirm}
              disabled={busy}
              className="w-full rounded-full bg-pro hover:bg-pro-hover text-white py-6 text-base font-semibold"
              data-testid="mollie-confirm-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Traitement…</> : "Simuler un paiement réussi"}
            </Button>
            <Button
              onClick={cancel}
              disabled={busy}
              variant="outline"
              className="w-full rounded-full py-6"
              data-testid="mollie-cancel-btn"
            >
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
